/**
 * TechnicalScannerPage — 10 automated chart pattern detectors
 *
 * Sources:
 *   GET /api/scanner/technical/scan     → scan results
 *   GET /api/scanner/technical/patterns → pattern definitions
 */
import { useEffect, useMemo, useState, useCallback } from 'react'
import {
  ScanSearch, RefreshCw, Loader2, TrendingUp, TrendingDown,
  Filter, Info, ChevronDown, ChevronUp, Search,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '@/lib/api'

// ── Types ────────────────────────────────────────────────────────────────────

interface DetectedPattern {
  pattern: string
  direction: 'bullish' | 'bearish'
  confidence: number
  detail: string
}

interface ScanResult {
  ticker: string
  price?: number
  patterns: DetectedPattern[]
}

interface ScanResponse {
  results: ScanResult[]
  scan_time: string
}

interface PatternDef {
  name: string
  description: string
}

interface PatternsResponse {
  patterns: PatternDef[]
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function directionColor(d: string) {
  return d === 'bullish'
    ? 'text-emerald-light bg-emerald/10 border-emerald/20'
    : 'text-rose-light bg-rose/10 border-rose/20'
}

function confidenceBar(pct: number) {
  const clamped = Math.max(0, Math.min(100, pct))
  let color = 'bg-rose'
  if (clamped >= 75) color = 'bg-emerald'
  else if (clamped >= 50) color = 'bg-amber'
  else if (clamped >= 25) color = 'bg-amber-dark'
  return { clamped, color }
}

// ── Component ────────────────────────────────────────────────────────────────

export default function TechnicalScannerPage() {
  const [results, setResults] = useState<ScanResult[]>([])
  const [scanTime, setScanTime] = useState<string | null>(null)
  const [patternDefs, setPatternDefs] = useState<PatternDef[]>([])
  const [loading, setLoading] = useState(true)
  const [scanning, setScanning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filterPattern, setFilterPattern] = useState<string>('all')
  const [searchTicker, setSearchTicker] = useState('')
  const [showLegend, setShowLegend] = useState(false)

  // Fetch pattern definitions once
  useEffect(() => {
    api.get<PatternsResponse>('/api/scanner/technical/patterns')
      .then(r => setPatternDefs(r.data.patterns ?? []))
      .catch(() => {})
  }, [])

  // Initial scan
  const fetchScan = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<ScanResponse>('/api/scanner/technical/scan')
      setResults(res.data.results ?? [])
      setScanTime(res.data.scan_time ?? null)
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load scan results')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchScan() }, [fetchScan])

  // Manual "Scan Now"
  const handleScanNow = async () => {
    setScanning(true)
    setError(null)
    try {
      const res = await api.get<ScanResponse>('/api/scanner/technical/scan')
      setResults(res.data.results ?? [])
      setScanTime(res.data.scan_time ?? null)
    } catch (err: any) {
      setError(err?.message ?? 'Scan failed')
    } finally {
      setScanning(false)
    }
  }

  // Build unique pattern names from results for filter dropdown
  const patternNames = useMemo(() => {
    const set = new Set<string>()
    for (const r of results) for (const p of r.patterns) set.add(p.pattern)
    return Array.from(set).sort()
  }, [results])

  // Filtered results
  const filtered = useMemo(() => {
    let out = results
    if (searchTicker.trim()) {
      const q = searchTicker.trim().toUpperCase()
      out = out.filter(r => r.ticker.toUpperCase().includes(q))
    }
    if (filterPattern !== 'all') {
      out = out
        .map(r => ({ ...r, patterns: r.patterns.filter(p => p.pattern === filterPattern) }))
        .filter(r => r.patterns.length > 0)
    }
    return out
  }, [results, filterPattern, searchTicker])

  // ── Skeleton ─────────────────────────────────────────────────────────────
  if (loading && results.length === 0) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="h-8 w-72 bg-surface-card rounded animate-pulse" />
        <div className="h-12 bg-surface-card rounded-xl animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-48 bg-surface-card rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (error && results.length === 0) {
    return (
      <div className="card p-6 text-center">
        <p className="text-rose-light">{error}</p>
        <button type="button" onClick={fetchScan} className="btn-secondary mt-3">Retry</button>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <ScanSearch size={20} className="text-blue-light" aria-hidden />
            Technical Scanner
          </h1>
          <p className="text-sm text-text-secondary mt-1">10 automated chart pattern detectors</p>
        </div>
        <div className="flex items-center gap-3">
          {scanTime && (
            <span className="text-xs text-text-muted">
              Last scan: {new Date(scanTime).toLocaleTimeString()}
            </span>
          )}
          <button
            type="button"
            onClick={handleScanNow}
            disabled={scanning}
            className="btn-secondary flex items-center gap-2"
          >
            {scanning ? <Loader2 size={14} className="animate-spin" aria-hidden /> : <RefreshCw size={14} aria-hidden />}
            Scan Now
          </button>
        </div>
      </div>

      {/* Toolbar: search + filter + legend toggle */}
      <div className="glass rounded-xl px-3 py-2 flex flex-wrap items-center gap-3">
        {/* Ticker search */}
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" aria-hidden />
          <input
            type="text"
            value={searchTicker}
            onChange={e => setSearchTicker(e.target.value)}
            placeholder="Filter ticker..."
            aria-label="Filter by ticker"
            className="input pl-8 pr-3 py-2 w-48"
          />
        </div>

        {/* Pattern filter */}
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-text-muted" aria-hidden />
          <select
            value={filterPattern}
            onChange={e => setFilterPattern(e.target.value)}
            aria-label="Filter by pattern"
            className="input px-3 py-2 w-auto"
          >
            <option value="all">All Patterns</option>
            {patternNames.map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>

        {/* Legend toggle */}
        <button
          type="button"
          onClick={() => setShowLegend(v => !v)}
          className="btn-secondary flex items-center gap-1.5 text-xs"
        >
          <Info size={13} aria-hidden />
          Pattern Legend
          {showLegend ? <ChevronUp size={12} aria-hidden /> : <ChevronDown size={12} aria-hidden />}
        </button>

        {/* Count */}
        <span className="ml-auto badge-blue">
          {filtered.length} ticker{filtered.length !== 1 ? 's' : ''} with patterns
        </span>
      </div>

      {/* Pattern Legend */}
      {showLegend && patternDefs.length > 0 && (
        <div className="card p-4">
          <h2 className="panel-title mb-3">Pattern Legend</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {patternDefs.map(pd => (
              <div key={pd.name} className="flex gap-2">
                <span className="text-xs font-mono font-semibold text-blue-light whitespace-nowrap">{pd.name}</span>
                <span className="text-xs text-text-muted">{pd.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Results grid */}
      {filtered.length === 0 ? (
        <div className="card p-10 text-center">
          <ScanSearch size={32} className="mx-auto text-text-muted mb-3" aria-hidden />
          <p className="text-text-muted text-sm">No patterns detected for current filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 stagger-children">
          {filtered.map(r => (
            <TickerCard key={r.ticker} result={r} />
          ))}
        </div>
      )}
    </div>
  )
}

// ── Ticker Card ──────────────────────────────────────────────────────────────

function TickerCard({ result }: { result: ScanResult }) {
  const bullish = result.patterns.filter(p => p.direction === 'bullish').length
  const bearish = result.patterns.length - bullish

  return (
    <div className="card-hover p-4 space-y-3">
      {/* Ticker header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-bold text-text-primary">{result.ticker}</h3>
          {result.price != null && (
            <p className="text-xs text-text-muted font-mono">
              ${result.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs">
          {bullish > 0 && (
            <span className="flex items-center gap-0.5 text-emerald-light">
              <TrendingUp size={12} aria-hidden /> {bullish}
            </span>
          )}
          {bearish > 0 && (
            <span className="flex items-center gap-0.5 text-rose-light">
              <TrendingDown size={12} aria-hidden /> {bearish}
            </span>
          )}
        </div>
      </div>

      {/* Pattern list */}
      <div className="space-y-2">
        {result.patterns.map((p, idx) => {
          const { clamped, color } = confidenceBar(p.confidence)
          return (
            <div key={`${p.pattern}-${idx}`} className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                {/* Pattern badge */}
                <span className={clsx(
                  'text-[10px] font-semibold px-2 py-0.5 rounded border',
                  directionColor(p.direction),
                )}>
                  {p.direction === 'bullish'
                    ? <TrendingUp size={10} className="inline mr-1" aria-hidden />
                    : <TrendingDown size={10} className="inline mr-1" aria-hidden />
                  }
                  {p.pattern}
                </span>

                {/* Confidence bar */}
                <div className="flex items-center gap-1.5 flex-1 min-w-[80px]">
                  <div className="flex-1 h-1.5 rounded-full bg-surface-hover overflow-hidden">
                    <div
                      className={clsx('h-full rounded-full transition-all', color)}
                      style={{ width: `${clamped}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-text-muted font-mono w-8 text-right">{Math.round(clamped)}%</span>
                </div>
              </div>

              {/* Detail */}
              {p.detail && (
                <p className="text-[11px] text-text-muted pl-1 leading-snug">{p.detail}</p>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
